// =============================================================================
//  PdfViewerHost — drawer global de visualisation PDF in-app.
//
//  Monté UNE fois dans App (couvre les 3 portails + pages publiques).
//  S'enregistre auprès de lib/pdfViewer : tout appel à openInvoicePdf
//  (onglet Factures, Relevé MémoBank, portail client…) ouvre ici au lieu
//  d'un nouvel onglet. Esc / clic sur le fond / ✕ pour fermer ;
//  « Ouvrir dans un onglet » conserve l'ancien comportement (zoom natif,
//  impression). Rendu PDF = visionneuse native du navigateur via iframe.
// =============================================================================
import { useCallback, useEffect, useState } from 'react'
import { registerPdfViewer, type PdfViewerRequest } from '../lib/pdfViewer'

export default function PdfViewerHost() {
  const [req, setReq] = useState<PdfViewerRequest | null>(null)

  useEffect(() => {
    registerPdfViewer(setReq)
    return () => registerPdfViewer(null)
  }, [])

  const close = useCallback(() => setReq(null), [])

  // Le lecteur PDF natif s'affiche dans une iframe (contexte séparé) : dès qu'on
  // clique/scrolle dans le PDF, le focus part dans l'iframe et un 1er clic sur la
  // croix ou le fond ne fait que le ramener dans la page — d'où la fermeture « en
  // deux clics ». On neutralise ce focus sur tout appui souris dans le viewer, en
  // écoutant le pointerdown en phase capture sur le document parent : il précède
  // le clic quelle que soit la position du curseur (onMouseEnter ratait un curseur
  // déjà posé sur la croix). Les évènements internes au PDF restent dans l'iframe
  // et n'atteignent pas ce listener : scroll et sélection dans le PDF sont intacts.
  useEffect(() => {
    if (!req) return
    const restoreFocus = () => {
      const el = document.activeElement
      if (el instanceof HTMLIFrameElement) el.blur()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', restoreFocus, true)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', restoreFocus, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [req, close])

  if (!req) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-[70]" onClick={close} />

      <div className="fixed right-0 top-0 bottom-0 w-[min(920px,94vw)] bg-surface-card shadow-2xl z-[71] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-ink-ghost/30">
          <h2 className="text-body font-semibold text-ink truncate">{req.title}</h2>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => window.open(req.url, '_blank')}
              className="text-caption text-ink-secondary hover:text-ink transition-colors px-2 py-1 rounded border border-ink-ghost/40 cursor-pointer"
            >
              Ouvrir dans un onglet
            </button>
            <button
              type="button"
              onClick={close}
              aria-label="Fermer"
              className="text-ink-muted hover:text-ink transition-colors px-2 py-1 cursor-pointer"
            >
              ✕
            </button>
          </div>
        </div>

        <iframe src={req.url} title={req.title} className="flex-1 w-full border-0" />
      </div>
    </>
  )
}
